import { connectorIdSchema } from '@hugo/core'
import { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { z } from 'zod'
import { AppShell } from '@/components/app-shell'
import { ErrorView } from '@/components/error-view'
import { Button } from '@/components/ui/button'
import { View } from '@/components/view'
import { ApiError } from '@/lib/api'
import { tagFilterSchema } from '@/lib/tags'
import { ActivityPage } from '@/routes/activity'
import { BoardPage } from '@/routes/board'
import { DonePage, InboxPage, SnoozedPage, TasksPage } from '@/routes/lists'
import { LoginPage } from '@/routes/login'
import { OutboxPage } from '@/routes/outbox'
import { SettingsPage, SettingsSectionPage } from '@/routes/settings'
import { TodayPage } from '@/routes/today'

export type RouterContext = {
  queryClient: QueryClient
  auth: { signedIn: boolean }
}

function NotFound() {
  return (
    <View name="not-found" title="Page not found" description="This page does not exist.">
      <div>
        <Button variant="outline" render={<Link to="/today" />}>
          Back to Today
        </Button>
      </div>
    </View>
  )
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login',
  validateSearch: z.object({
    error: z.string().max(64).optional().catch(undefined),
    redirect: z.string().max(512).optional().catch(undefined),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.signedIn) {
      const to =
        search.redirect?.startsWith('/') && !search.redirect.startsWith('//')
          ? search.redirect
          : '/'
      throw redirect({ to })
    }
  },
  component: function Login() {
    const { error } = loginRoute.useSearch()
    return <LoginPage error={error} />
  },
})

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => {
    if (!context.auth.signedIn) {
      throw redirect({
        to: '/login',
        search: { redirect: location.pathname === '/' ? undefined : location.href },
      })
    }
  },
  component: AppShell,
  notFoundComponent: NotFound,
})

/** hugo opens on Today (decision 92). */
const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/today', replace: true })
  },
})
const inboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'inbox',
  component: InboxPage,
})
const todayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'today',
  component: TodayPage,
})
/** `tag`: only the cards with this tag (decision 97). */
const boardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'board',
  validateSearch: z.object({ tag: tagFilterSchema }),
  component: function Board() {
    const { tag } = boardRoute.useSearch()
    return <BoardPage tag={tag} />
  },
})
/** One list of everything the user is responsible for; `filter` shows I owe or Waiting on. */
const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'tasks',
  validateSearch: z.object({
    filter: z.enum(['owe', 'waiting']).optional().catch(undefined),
    tag: tagFilterSchema,
  }),
  component: function Tasks() {
    const { filter, tag } = tasksRoute.useSearch()
    return <TasksPage filter={filter ?? 'all'} tag={tag} />
  },
})
/** The former I owe and Waiting on lists: the one list, filtered (old links keep working). */
const oweRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'owe',
  beforeLoad: () => {
    throw redirect({ to: '/tasks', search: { filter: 'owe' }, replace: true })
  },
})
const waitingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'waiting',
  beforeLoad: () => {
    throw redirect({ to: '/tasks', search: { filter: 'waiting' }, replace: true })
  },
})
const outboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'outbox',
  component: OutboxPage,
})
const snoozedRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'snoozed',
  component: SnoozedPage,
})
const doneRoute = createRoute({ getParentRoute: () => appRoute, path: 'done', component: DonePage })
const activityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'activity',
  component: ActivityPage,
})
/** Settings: an overview of every section with what it is set to now. */
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'settings',
  /** Old links: Google's callback used to land here. Send it on to Apps. */
  validateSearch: z.object({
    connect: connectorIdSchema.optional().catch(undefined),
    error: z.string().max(64).optional().catch(undefined),
  }),
  beforeLoad: ({ search }) => {
    if (search.connect || search.error) {
      throw redirect({
        to: '/settings/$section',
        params: { section: 'apps' },
        search: { connect: search.connect, error: search.error },
        replace: true,
      })
    }
  },
  component: SettingsPage,
})
/**
 * One section of Settings. On Apps, `connect`: Google sent the user back after granting access
 * (connect once, then drop it); `error`: Google or better-auth reported why access was not granted.
 * `connected` / `connector` + `error`: hugo's OAuth callback (GitHub, …) says how it went.
 */
const settingsSectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'settings/$section',
  validateSearch: z.object({
    connect: connectorIdSchema.optional().catch(undefined),
    connected: connectorIdSchema.optional().catch(undefined),
    connector: connectorIdSchema.optional().catch(undefined),
    error: z.string().max(64).optional().catch(undefined),
  }),
  component: function SettingsSection() {
    const { section } = settingsSectionRoute.useParams()
    const { connect, connected, connector, error } = settingsSectionRoute.useSearch()
    // The OAuth callback names the connector; Google's names none (it comes with `connect`).
    const oauth = connected
      ? { connector: connected, error: null }
      : connector && error
        ? { connector, error }
        : undefined
    return (
      <SettingsSectionPage
        section={section}
        connect={connect}
        linkError={oauth ? undefined : error}
        oauthResult={oauth}
      />
    )
  },
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  appRoute.addChildren([
    homeRoute,
    inboxRoute,
    todayRoute,
    boardRoute,
    tasksRoute,
    oweRoute,
    waitingRoute,
    outboxRoute,
    snoozedRoute,
    doneRoute,
    activityRoute,
    settingsRoute,
    settingsSectionRoute,
  ]),
])

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // Retrying cannot fix a 4xx (not found, invalid input, signed out): show the error now.
      retry: (failures, error) =>
        !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failures < 3,
    },
  },
})

export const router = createRouter({
  routeTree,
  context: { queryClient, auth: { signedIn: false } },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultErrorComponent: ({ error, reset }) => <ErrorView error={error} reset={reset} />,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
